import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialShareAccessDeniedStep from 'containers/SocialShareModal/SocialShareAccessDeniedStep';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useYouTubeAuthenticator from 'hooks/useYouTubeAuthenticator';
import {
  scheduleYouTubeVideo,
  shareYouTubeVideo,
} from 'redux/modules/download';
import { showError } from 'redux/modules/notification';
import {
  getYoutubeCategories,
  getYoutubeLanguages,
  getYoutubePlaylists,
  youtubePlaylistsSelector,
  youtubeSelector,
} from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import {
  useYouTubeAdditionalDetailsStep,
  useYouTubeChannelNeededStep,
  useYouTubeComposerStep,
  useYouTubeCreatePlaylistStep,
  useYouTubeDisallowedAccountStep,
  useYouTubeElegibleAccountStep,
  useYouTubeVerificationProgressStep,
  useYouTubeVerificationStep,
} from './steps';
import useYouTubeComposerValues from './steps/useYouTubeComposerValues';
import { YouTubeSocialShareStep } from './types';

export default function useYouTubeSocialShare(): SocialSharePlatformData<
  YouTubeSocialShareStep
> {
  const { providerUserId, profileImageUrl, username, postStatus } = useSelector(
    youtubeSelector,
  );
  const { playlists } = useSelector(youtubePlaylistsSelector);

  const dispatch = useDispatch<Dispatch>();

  const {
    description,
    activeStep,
    onAuthSuccess,
    onAuthError,
    goToStep,
  } = useSocialShareModal();

  const {
    title,
    selectedPlaylistsIds,
    visibility,
    madeForKids,
    categoryId,
    language,
    tags,
    onTitleChange,
    onPlaylistChange,
    onVisibilityChange,
    onMadeForKidsChange,
    onCategoryIdChange,
    onLanguageChange,
    onTagsChange,
    resetComposerValues,
  } = useYouTubeComposerValues();

  const handleAuthSuccess = useCallback(() => {
    onAuthSuccess();

    dispatch(getYoutubeCategories()).catch(() => {
      dispatch(
        showError({
          message: 'Unable to load categories due to network error',
        }),
      );
    });

    dispatch(getYoutubeLanguages()).catch(() => {
      dispatch(
        showError({
          message: 'Unable to load languages due to network error',
        }),
      );
    });

    dispatch(getYoutubePlaylists());

    goToStep('composer');
  }, [dispatch, onAuthSuccess, goToStep]);

  const { authenticating, authenticate } = useYouTubeAuthenticator({
    force: true,
    onAuthSuccess: handleAuthSuccess,
    onError: error => {
      onAuthError(error);
      goToStep('access-denied');
    },
  });

  const handleShare = useOnShare({
    shareNow: useCallback(
      () =>
        dispatch(
          shareYouTubeVideo({
            language,
            title,
            tags,
            madeForKids,
            categoryId,
            visibility,
            playlists,
            selectedPlaylistsIds,
            description,
          }),
        ),
      [
        categoryId,
        description,
        dispatch,
        language,
        madeForKids,
        playlists,
        selectedPlaylistsIds,
        tags,
        title,
        visibility,
      ],
    ),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(
          scheduleYouTubeVideo({
            language,
            title,
            tags,
            madeForKids,
            categoryId,
            visibility,
            playlists,
            selectedPlaylistsIds,
            description,
            scheduledDate,
          }),
        ),
      [
        categoryId,
        description,
        dispatch,
        language,
        madeForKids,
        playlists,
        selectedPlaylistsIds,
        tags,
        title,
        visibility,
      ],
    ),
  });

  const youTubeAccessDeniedStep = useSocialShareAccessDeniedStep({
    onCancel: () => goToStep('composer'),
  });

  const youTubeDisallowedAccountStep = useYouTubeDisallowedAccountStep();

  const youTubeElegibleAccountStep = useYouTubeElegibleAccountStep();

  const youTubeChannelNeededStep = useYouTubeChannelNeededStep();

  const youTubeVerificationStep = useYouTubeVerificationStep({
    onAuthSuccess: handleAuthSuccess,
  });

  const youTubeVerificationProgressStep = useYouTubeVerificationProgressStep();

  const youTubeCreatePlaylistStep = useYouTubeCreatePlaylistStep();

  const youTubeComposerStep = useYouTubeComposerStep({
    title,
    selectedPlaylistsIds,
    onTitleChange,
    onPlaylistChange,
    onShare: handleShare,
  });

  const youTubeAdditionalDetailsStep = useYouTubeAdditionalDetailsStep({
    visibility,
    madeForKids,
    categoryId,
    language,
    tags,
    isActive: activeStep === 'additional-details',
    onVisibilityChange,
    onMadeForKidsChange,
    onCategoryIdChange,
    onLanguageChange,
    onTagsChange,
    resetComposerValues,
  });

  return {
    platformAlias: 'youtube',
    authenticating: authenticating || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [
      youTubeAccessDeniedStep,
      youTubeDisallowedAccountStep,
      youTubeElegibleAccountStep,
      youTubeChannelNeededStep,
      youTubeVerificationStep,
      youTubeVerificationProgressStep,
      youTubeCreatePlaylistStep,
      youTubeComposerStep,
      youTubeAdditionalDetailsStep,
    ],
    defaultStep: youTubeVerificationProgressStep.id,
    postStatus,
    authenticate,
  };
}
